'use client'

import Link from 'next/link'
import { MC, SERIF } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { coverageT } from '@/lib/mycio/coverageI18n'
import { coverageTopics } from '@/lib/mycio/marketingCoverage'
import { localizedCountryName, type CountryDetail } from '@/lib/mycio/coverageDetail'
import { BODY, CARD, EYEBROW, EstimateFooter, Flag, H1, H2, H3, LEAD, TierPill } from './parts'

/**
 * /coverage, rendered in the reader's language. A client component because the language lives in a cookie
 * the selector changes on the client; the server page (app/(mycio)/coverage/page.tsx) keeps the metadata and
 * hands the data down. Every sentence goes through `t.s()` — machine-translated at build time, English when
 * no translation exists — and the shell says so above the page.
 */
export default function CoverageIndexView({ countries }: { countries: CountryDetail[] }) {
  const { language } = useMyCIOLanguage()
  const t = coverageT(language)
  return (
    <>
      <section className="mc-container mc-pad-hero" style={{ maxWidth: 1180, margin: '0 auto', padding: '84px 32px 40px' }}>
        <p style={EYEBROW}>{t.ui('indexEyebrow')}</p>
        <h1 className="mc-finalcta-h" style={{ ...H1, maxWidth: 820 }}>
          {t.ui('indexTitleBefore')}<em style={{ fontStyle: 'italic', color: MC.forest }}>{t.ui('indexTitleEm')}</em>{t.ui('indexTitleAfter')}
        </h1>
        <p style={LEAD}>{t.ui('indexLead')}</p>
        <div style={{ display: 'flex', gap: '12px 28px', flexWrap: 'wrap', fontSize: 13.5, color: MC.muted2 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: MC.forest }} />{t.ui('validated')} — {t.ui('validatedNote')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: MC.clay }} />{t.ui('provisional')} — {t.ui('provisionalNote')}</span>
        </div>
      </section>

      <section className="mc-container" style={{ maxWidth: 1180, margin: '0 auto', padding: '16px 32px 56px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          {countries.map((c) => (
            <Link key={c.code} href={`/coverage/${c.slug}`} className="mc-flagchip" style={{ ...CARD, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 12, textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                  <Flag slug={c.slug} />
                  <span style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 500, color: MC.ink }}>{localizedCountryName(c.code, language, c.name)}</span>
                </span>
                <TierPill tier={c.tier} t={t} />
              </div>
              <p style={{ ...BODY, fontSize: 14.5 }}>
                {c.memberSentence ? t.s(c.memberSentence) : t.ui('validatedCardSentence')}
              </p>
              <span style={{ fontSize: 14, fontWeight: 600, color: MC.forest }}>{t.ui('openCountry')}</span>
            </Link>
          ))}
        </div>
      </section>

      <section style={{ background: '#EEE8DC', borderTop: `1px solid ${MC.border}`, borderBottom: `1px solid ${MC.border}` }}>
        <div className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '72px 32px' }}>
          <p style={EYEBROW}>{t.ui('enginesEyebrow')}</p>
          <h2 style={H2}>{t.ui('sixAreasTitle')}</h2>
          <p style={{ ...LEAD, marginBottom: 32 }}>{t.ui('sixAreasLead')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
            {coverageTopics.map((tp) => (
              <article key={tp.title} style={CARD}>
                <h3 style={H3}>{t.s(tp.title)}</h3>
                <p style={{ ...BODY, fontSize: 14.5, marginBottom: 8 }}><strong style={{ color: MC.forest }}>{t.ui('included')}</strong> {t.s(tp.included)}</p>
                <p style={{ ...BODY, fontSize: 14.5, marginBottom: 8 }}><strong style={{ color: MC.inkSoft }}>{t.ui('assumed')}</strong> {t.s(tp.assumption)}</p>
                <p style={{ ...BODY, fontSize: 14.5 }}><strong style={{ color: MC.clay }}>{t.ui('notCovered')}</strong> {t.s(tp.excluded)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mc-container" style={{ maxWidth: 1180, margin: '0 auto', padding: '16px 32px 96px' }}>
        <EstimateFooter t={t} />
      </section>
    </>
  )
}
